import { useCallback } from 'react';
import { SMSTag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { useDismissTagMutation } from '../mutations';
import { useSMSDataV3QueryUpdaters } from '../query-updaters';

const DEFAULT_DELAY = 3500;

/**
 * Hook for dismissing a tag with undo functionality.
 *
 * Flow:
 * 1. Immediately removes tag optimistically (for instant UI feedback)
 * 2. Calls onUndoAvailable with an undo callback (typically to show "Tag dismissed. [Undo]" notification)
 * 3. After delay + 1000ms, actually submits the dismiss mutation (if not cancelled)
 * 4. If user clicks undo, the undo callback re-adds the tag and cancels the mutation
 */
type UseDismissTagWithUndoArgs = {
  /** Called when undo becomes available. Typically used to show a notification with an undo button. */
  onUndoAvailable: (undoCallback: () => void, autoDismissAfter: number) => void;
  /** How long to wait before showing the undo option (default: 3500ms) */
  delay?: number;
  mutationOptions?: Parameters<typeof useDismissTagMutation>[0];
  onDismissError?: () => void;
};

export const useDismissTagWithUndo = ({
  onUndoAvailable,
  delay = DEFAULT_DELAY,
  mutationOptions,
  onDismissError,
}: UseDismissTagWithUndoArgs) => {
  // Disable optimistic update in the mutation since we handle it ourselves
  const dismissTagMutation = useDismissTagMutation({
    ...mutationOptions,
    optimisticUpdate: false,
  });
  const { updateSMS } = useSMSDataV3QueryUpdaters();
  return useCallback(
    async (
      {
        originalAppliedBy,
        smsCreatedAt,
        ...request
      }: Parameters<typeof dismissTagMutation.mutate>[0] & { originalAppliedBy: string; smsCreatedAt: string },
      options?: Parameters<typeof dismissTagMutation.mutate>[1]
    ) => {
      // Store original SMS for potential revert
      // eslint-disable-next-line prefer-const
      let originalSMS: any;

      const timeout = setTimeout(() => {
        // Pass the revert function to the mutation so onError can use it
        dismissTagMutation.mutate(request, {
          ...options,
          onError: (error, variables, context) => {
            // Revert the optimistic update done by useDismissTagWithUndo
            if (originalSMS) {
              updateSMS({
                matchValues: {
                  id: request.smsId,
                  threadId: request.threadId,
                  locationId: request.groupId,
                },
                newValues: originalSMS,
              });
            }

            // Call the original onError if provided
            options?.onError?.(error, variables, context);
            onDismissError?.();
          },
        });
      }, delay + 1000);

      // Use V3 SMS Data
      const updateResult = updateSMS({
        matchValues: {
          id: request.smsId,
          threadId: request.threadId,
          locationId: request.groupId,
        },
        newValues: (sms) => {
          return {
            ...sms,
            tags: sms.tags.filter((id) => id !== request.tagId),
            tagsDetailed: sms.tagsDetailed?.filter((tag) => tag.tagId !== request.tagId),
          };
        },
        skipInvalidation: true,
      });

      // Store original SMS for potential revert
      originalSMS = updateResult.originalSMS;

      // Call onUndoAvailable with the undo callback - this typically shows a notification with an "Undo" button
      onUndoAvailable(() => {
        clearTimeout(timeout); // Cancel the actual dismiss mutation

        updateSMS({
          matchValues: {
            id: request.smsId,
            threadId: request.threadId,
            locationId: request.groupId,
          },
          newValues: (sms) => {
            const newTagsDetailed: (typeof sms)['tagsDetailed'] = sms.tagsDetailed?.some(
              (tag) => tag.tagId === request.tagId
            )
              ? sms.tagsDetailed
              : [
                  ...(sms.tagsDetailed ?? []),
                  {
                    tagId: request.tagId,
                    smsId: request.smsId,
                    appliedBy: originalAppliedBy,
                    smsCreatedAt,
                  } satisfies SMSTag,
                ];
            return {
              ...sms,
              tags: Array.from(new Set([...sms.tags, request.tagId])),
              tagsDetailed: newTagsDetailed,
            };
          },
        });
      }, delay);
    },
    [delay, dismissTagMutation, updateSMS, onUndoAvailable]
  );
};
